import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { DialogFooter, DialogHeader, Modal } from '../Modal.js'
import { draftFromJob, draftFromSchedule, type RepeatForJob, type ScheduleView } from '../repeat/drafts.js'
import { RuleFields } from '../repeat/RuleFields.js'
import { nextVisitSentence, ruleInput, ruleProblem, type RuleDraft } from '../repeat/rule.js'
import { Button, ErrorState, Field, Input, Select } from '../ui.js'
import type { Job } from './types.js'

export type RepeatPopupMode =
  | { kind: 'start'; legacyRecurrenceId: string | null }
  | { kind: 'edit'; schedule: ScheduleView }

/**
 * "Make this job repeat" — and the same window editing a schedule. Decision 96.
 *
 * Starting makes this job visit 1 of a schedule, due the day it is booked for.
 * From an imported ServiceM8 series it takes the series over, prefilled with
 * its interval. It can also put the schedule in one of the client's service
 * agreements. A new agreement is the builder's, opened from here with this job.
 */
export function RepeatPopup({
  open,
  mode,
  job,
  data,
  onClose,
  onNewAgreement,
}: {
  open: boolean
  mode: RepeatPopupMode
  job: Job
  data: RepeatForJob
  onClose: () => void
  onNewAgreement?: () => void
}) {
  return (
    <Modal open={open} onClose={onClose} labelledBy="repeat-popup-title" className="max-w-[600px]">
      {open ? (
        <RepeatForm
          key={mode.kind === 'edit' ? mode.schedule.id : 'start'}
          mode={mode}
          job={job}
          data={data}
          onClose={onClose}
          onNewAgreement={onNewAgreement}
        />
      ) : null}
    </Modal>
  )
}

function RepeatForm({
  mode,
  job,
  data,
  onClose,
  onNewAgreement,
}: {
  mode: RepeatPopupMode
  job: Job
  data: RepeatForJob
  onClose: () => void
  onNewAgreement?: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()

  const [draft, setDraft] = useState<RuleDraft>(() =>
    mode.kind === 'edit' ? draftFromSchedule(mode.schedule) : draftFromJob(job, data.imported),
  )
  const [firstDueOn, setFirstDueOn] = useState(() =>
    mode.kind === 'edit' ? mode.schedule.firstDueOn : data.jobDueOn,
  )
  const [agreement, setAgreement] = useState<string>('')

  const agreements = useQuery({
    ...trpc.agreements.list.queryOptions({ clientId: job.client.id }),
    enabled: mode.kind === 'start' && can('CLIENT_VIEW'),
  })
  const open = (agreements.data ?? []).filter((row) => row.status !== 'ENDED')

  const onSuccess = () => {
    void queryClient.invalidateQueries(trpc.repeat.pathFilter())
    void queryClient.invalidateQueries(trpc.agreements.pathFilter())
    onClose()
  }
  const start = useMutation(trpc.repeat.start.mutationOptions({ onSuccess }))
  const update = useMutation(trpc.repeat.update.mutationOptions({ onSuccess }))
  const error = start.error ?? update.error

  const problem = ruleProblem(draft)

  const save = () => {
    if (problem) return
    if (mode.kind === 'edit') {
      update.mutate({ id: mode.schedule.id, firstDueOn, ...ruleInput(draft) })
      return
    }
    start.mutate({
      jobId: job.id,
      legacyRecurrenceId: mode.legacyRecurrenceId,
      ...ruleInput(draft),
      ...(agreement ? { agreementId: agreement } : {}),
    })
  }

  // Visit 1's day when starting; the latest visit with a job when editing, so
  // the sentence says what happens next rather than what happened first.
  const fromDay =
    mode.kind === 'edit'
      ? ([...mode.schedule.slots].reverse().find((slot) => slot.job)?.dueOn ?? firstDueOn)
      : data.jobDueOn

  return (
    <>
      <DialogHeader
        id="repeat-popup-title"
        title={mode.kind === 'edit' ? 'Edit the repeat schedule' : 'Make this job repeat'}
        onClose={onClose}
      />

      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-5">
        {mode.kind === 'start' && mode.legacyRecurrenceId ? (
          <p className="rounded-lg bg-ink-50 px-3 py-2 text-[13px] text-ink-600">
            Takes over the repeating series imported from ServiceM8. Its earlier jobs stay on the
            timeline as they are.
          </p>
        ) : null}

        <RuleFields draft={draft} onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))} />

        {mode.kind === 'edit' ? (
          <Field
            label="First visit due"
            htmlFor="repeat-first-due"
            hint="Every visit's date is counted from this one. Visits already made keep theirs."
          >
            <Input
              id="repeat-first-due"
              type="date"
              value={firstDueOn}
              onChange={(event) => setFirstDueOn(event.target.value)}
              className="w-48"
            />
          </Field>
        ) : can('CLIENT_VIEW') ? (
          <div className="flex flex-col gap-3 border-t border-ink-200 pt-5">
            <Field
              label="Service agreement"
              htmlFor="repeat-agreement"
              hint="Optional. An agreement groups a client's sites and services under one term."
            >
              <Select
                id="repeat-agreement"
                value={agreement}
                onChange={(event) => setAgreement(event.target.value)}
              >
                <option value="">Not part of an agreement</option>
                {open.map((row) => (
                  <option key={row.id} value={row.id}>
                    {`AG-${row.number} · ${row.name}`}
                  </option>
                ))}
              </Select>
            </Field>
            {onNewAgreement ? (
              <button
                type="button"
                onClick={onNewAgreement}
                className="self-start text-[12.5px] font-semibold text-brand-700 hover:underline"
              >
                Set up a new service agreement with this job instead
              </button>
            ) : null}
          </div>
        ) : null}

        {error ? <ErrorState message={errorMessage(error)} /> : null}
      </div>

      <DialogFooter hint={problem ?? nextVisitSentence(draft, fromDay, data.settings.leadDays)}>
        <Button size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="primary"
          disabled={problem !== null}
          loading={start.isPending || update.isPending}
          onClick={save}
        >
          {mode.kind === 'edit' ? 'Save schedule' : 'Start repeating'}
        </Button>
      </DialogFooter>
    </>
  )
}
